import { Badge } from "@/components/ui/badge";
import { Network, Database, FlaskConical, Wrench, X, CheckCircle2, Coins } from "lucide-react";

const TOOL_LABELS = {
  query_flavor_graph: "Grafo (Cypher)",
  calculate_pairing_score: "Sinergia",
  find_chemical_substitutes: "Substitutos",
  search_scientific_library: "Acervo (Chroma)",
};

export default function RagToolsInspector({ meta, onClose }) {
  const tools = meta?.tool_calls || [];
  const sources = meta?.sources || [];
  const gc = meta?.graph_context || {};
  const edges = gc.edges || [];
  const nodesById = Object.fromEntries((gc.nodes || []).map((n) => [n.element_id, n.properties?.name]));

  const Section = ({ icon: Icon, title, children, testId }) => (
    <div className="border-b border-[#2A364F] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-slate-400">
        <Icon className="h-3.5 w-3.5 text-[#E05A2B]" /> {title}
      </div>
      {children}
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-[#0B0F17] border-l border-[#2A364F]" data-testid="rag-inspector-drawer">
      <div className="flex items-center justify-between px-4 h-12 border-b border-[#2A364F] shrink-0">
        <span className="text-sm font-medium text-slate-200">Inspector RAG & Tools</span>
        {onClose && (
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="close-rag-inspector">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {!meta && (
          <p className="p-4 text-xs text-slate-500" data-testid="rag-inspector-empty">
            Envie uma mensagem ao SousChef AI para ver aqui as ferramentas executadas, os trechos recuperados e os scores de sinergia.
          </p>
        )}

        {meta && (
          <>
            {meta.auto_synced && (
              <Section icon={CheckCircle2} title="Sincronização & Custo" testId="inspector-sync-section">
                <div className="flex items-center gap-1.5 text-xs text-emerald-400 mb-2">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Harmonização sincronizada automaticamente
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  {typeof meta.synergy_index === "number" && (
                    <Badge variant="outline" className="border-[#E05A2B]/40 text-[#E05A2B]">
                      Sinergia {Math.round(meta.synergy_index * 100)}%
                    </Badge>
                  )}
                  {meta.cost_estimate && (
                    <span className="inline-flex items-center gap-1 text-slate-200" title={meta.cost_estimate.note}>
                      <Coins className="h-3 w-3 text-[#FACC15]" /> ~{meta.cost_estimate.currency} {meta.cost_estimate.total_cost}
                    </span>
                  )}
                </div>
                {meta.cost_estimate && (
                  <p className="text-[10px] text-slate-500 mt-1">{meta.cost_estimate.note}</p>
                )}
              </Section>
            )}

            <Section icon={Wrench} title="Ferramentas executadas" testId="inspector-tools-section">
              {tools.length === 0 && <p className="text-xs text-slate-500">Nenhuma ferramenta usada.</p>}
              <div className="space-y-2">
                {tools.map((t, i) => (
                  <div key={i} className="rounded-md bg-[#161E2E] border border-[#2A364F] p-2">
                    <div className="text-xs text-[#00E5FF] font-mono">{TOOL_LABELS[t.name] || t.name}</div>
                    {t.arguments && Object.keys(t.arguments).length > 0 && (
                      <pre className="text-[10px] text-slate-400 font-mono mt-1 whitespace-pre-wrap break-all">
                        {JSON.stringify(t.arguments)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={Network} title="Relações do grafo" testId="inspector-graph-section">
              {edges.length === 0 && <p className="text-xs text-slate-500">Sem relações consultadas.</p>}
              <div className="space-y-1">
                {edges.slice(0, 20).map((e, i) => (
                  <div key={i} className="text-[11px] text-slate-300 font-mono">
                    <span className="text-slate-100">{nodesById[e.start] || "?"}</span>
                    <span className="text-[#E05A2B]"> —[{e.type}]→ </span>
                    <span className="text-slate-100">{nodesById[e.end] || "?"}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={Database} title="Trechos do acervo (Chroma)" testId="inspector-sources-section">
              {sources.length === 0 && <p className="text-xs text-slate-500">Nenhum trecho recuperado.</p>}
              <div className="space-y-2">
                {sources.map((s) => (
                  <div key={s.marker} className="rounded-md bg-[#161E2E] border border-[#2A364F] p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#00E5FF] font-mono">[{s.marker}] {s.title || s.source || "fonte"}</span>
                      <Badge variant="outline" className="border-[#2A364F] text-slate-400">{Math.round((s.score || 0) * 100)}%</Badge>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 line-clamp-3">{s.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={FlaskConical} title="Entidades identificadas" testId="inspector-entities-section">
              <div className="flex flex-wrap gap-1.5">
                {(gc.entities_matched || []).map((e) => (
                  <span key={e} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono bg-cyan-950/60 border border-cyan-500/40 text-cyan-300">
                    {e}
                  </span>
                ))}
                {(gc.entities_matched || []).length === 0 && <p className="text-xs text-slate-500">—</p>}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
